import dayjs from "dayjs";
import { AttachmentType } from "../../features/personal-info/attachments/attachmentsStore";
import { UPLOAD_FILE_INVALID_EXTENSION } from "../../features/personal-info/attachments/validations/attachmentValidations";

import { CountryFormConfiguration } from "../constants";
import { DaThongBaoLogo } from "../../components/common/Logo/models/Logo";

import { SanitizeMode } from "../next-gen-config-wip";
import { defaultForbiddenWordsValidation } from "./defaults";
import { getAddressData } from "../../api/avs";

const VIETNAM_NAMES_REGEX =
  /^[A-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ\s\-'‘`]+$/;
const VIETNAM_ADDRESS_REGEX =
  /^[A-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ\s\d\-.,/']+$/;
const VIETNAM_ADDRESS_REGEX_WITHOUT_PARENTHESIS =
  /^[A-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ\s\d\-.,/']+$/;
const VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_REGEX =
  /[^a-zA-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ -‘]+/g;
const VIETNAM_OTHER_CHARACTERS_THAN_ADDRESS_REGEX =
  /[^A-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ\d\-.,/' ]+$/;
const VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_AND_DIGITS_REGEX =
  /[^a-zA-Za-zĂÂĐÊÔƠƯăâđêôơưÁÀẢÃẠẮẰẲẴẶẤẦẨẪẬÉÈẺẼẸẾỀỂỄỆÍÌỈĨỊÓÒỎÕỌỐỒỔỖỘỚỜỞỠỢÚÙỦŨỤỨỪỬỮỰÝỲỶỸỴáàảãạắằẳẵặấầẩẫậéèẻẽẹếềểễệíìỉĩịóòỏõọốồổỗộớờởỡợúùủũụứừửữựýỳỷỹỵ \d]+/g;
const VIETNAM_ID_CARD_NUMBER_REGEX = /^[0-9]{12}$/;

const getAddresses = async (areas: string[]) =>
  (await getAddressData("vi-VN", areas)).values;

const getCities = async (): Promise<string[]> => await getAddresses([]);

const getProvinces = async (selectedCity: string): Promise<string[]> =>
  await getAddresses([selectedCity]);

const getAddressLine2 = async (
  selectedCity: string,
  selectedProvince: string
): Promise<string[]> => await getAddresses([selectedCity, selectedProvince]);

const getPostalCodes = async (
  selectedCity: string,
  selectedProvince: string,
  selectedAddressLine2: string
): Promise<string[]> =>
  await getAddresses([selectedCity, selectedProvince, selectedAddressLine2]);

export const Vietnam: CountryFormConfiguration = {
  id: "vi-VN",
  forms: {
    personalInfo: {
      lastName: {
        name: "lastName",
        label: "LAST_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_REGEX, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "LAST_NAME_SIZE",
          },
          maxLength: {
            value: 30,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: VIETNAM_NAMES_REGEX,
            errorMessage: "LAST_NAME_SIZE",
          },
          trim: true,
        },
      },
      firstName: {
        name: "firstName",
        label: "FIRST_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_REGEX, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: 20,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: VIETNAM_NAMES_REGEX,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      permanentAddressLine1: {
        name: "permanentAddressLine1",
        label: "PERMANENT_ADDRESS",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 80,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: VIETNAM_ADDRESS_REGEX_WITHOUT_PARENTHESIS,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      permanentAddressCity: {
        name: "permanentAddressCity",
        label: "PERMANENT_CITY",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: [],
        getOptions: getCities,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      permanentAddressProvince: {
        name: "permanentAddressProvince",
        label: "PERMANENT_PROVINCE",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: ["permanentAddressCity"],
        getOptions: getProvinces,
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "permanentAddressCity",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      permanentAddressLine2: {
        name: "permanentAddressLine2",
        label: "PERMANENT_ADDRESS_LINE_2",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: ["permanentAddressCity", "permanentAddressProvince"],
        getOptions: getAddressLine2,
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "permanentAddressProvince",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
        },
      },
      permanentAddressPostalCode: {
        name: "permanentAddressPostalCode",
        label: "PERMANENT_POSTAL_CODE",
        type: "asyncAutocomplete",
        defaultValue: "",
        dependenciesNames: [
          "permanentAddressCity",
          "permanentAddressProvince",
          "permanentAddressLine2",
        ],
        getOptions: getPostalCodes,
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "permanentAddressLine2",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      otherMailingAddress: {
        name: "otherMailingAddress",
        label: "OTHER_MAILING_ADDRESS",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "text",
        rule: {
          keepValueOnHidden: true,
          visibleOn: [
            {
              visibilityDeterminingField: "otherMailingAddress",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 80,
            errorMessage: "ADDRESS_SIZE",
          },
          pattern: {
            value: VIETNAM_ADDRESS_REGEX,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "CITY",
        type: "asyncAutocomplete",
        rule: {
          keepValueOnHidden: true,
          visibleOn: [
            {
              visibilityDeterminingField: "otherMailingAddress",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        dependenciesNames: [],
        getOptions: getCities,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      province: {
        name: "province",
        label: "PROVINCE",
        type: "asyncAutocomplete",
        dependenciesNames: ["city"],
        getOptions: getProvinces,
        rule: {
          keepValueOnHidden: true,
          visibleOn: [
            {
              visibilityDeterminingField: "otherMailingAddress",
              visibleRuleFunction: (field) => Boolean(field),
            },
            {
              visibilityDeterminingField: "city",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      addressLine2: {
        name: "addressLine2",
        label: "ADDRESS_LINE_2",
        type: "asyncAutocomplete",
        dependenciesNames: ["city", "province"],
        getOptions: getAddressLine2,
        rule: {
          keepValueOnHidden: true,
          visibleOn: [
            {
              visibilityDeterminingField: "otherMailingAddress",
              visibleRuleFunction: (field) => Boolean(field),
            },
            {
              visibilityDeterminingField: "province",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "asyncAutocomplete",
        dependenciesNames: ["city", "province", "addressLine2"],
        getOptions: getPostalCodes,
        rule: {
          keepValueOnHidden: true,
          visibleOn: [
            {
              visibilityDeterminingField: "otherMailingAddress",
              visibleRuleFunction: (field) => Boolean(field),
            },
            {
              visibilityDeterminingField: "addressLine2",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dateOfBirth: {
        name: "dateOfBirth",
        label: "BIRTHDAY",
        type: "date",
        bottomDescription: { text: "BIRTHDAY_DISCLAIMER" },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxDate: {
            value: dayjs().subtract(18, "year"),
            errorMessage: "TOO_YOUNG",
          },
          minDate: {
            value: dayjs().subtract(120, "year"),
            errorMessage: "TOO_OLD",
          },
        },
      },
      registrationNumber0: {
        name: "registrationNumber0",
        label: "REGISTRATION_NUMBER0",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 12,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          minLength: {
            value: 12,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          pattern: {
            value: VIETNAM_ID_CARD_NUMBER_REGEX,
            errorMessage: "REGISTRATION_NUMBER_INVALID_PATTERN",
          },
          trim: true,
        },
      },
      registrationNumber0IssueDate: {
        name: "registrationNumber0IssueDate",
        label: "ISSUE_DATE",
        type: "date",
        bottomDescription: {
          text: "REGISTRATION_NUMBER_ISSUE_DATE_DISCLAIMER",
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxDate: {
            value: dayjs().subtract(1, "day"),
            errorMessage: "WRONG_ISSUE_DATE",
          },
        },
      },
      registrationNumber0Attachment0: {
        name: "registrationNumber0-attachment0",
        label: "UPLOAD_FILE_INFO_RESIDENT0",
        type: "file",
        index: 0,
        attachmentType: AttachmentType.AUTHORIZATION_DOCUMENT,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: [".pdf", ".bmp", ".jpg", ".jpeg", ".png"],
            errorMessage: UPLOAD_FILE_INVALID_EXTENSION,
          },
        },
      },
      registrationNumber0Attachment1: {
        name: "registrationNumber0-attachment1",
        label: "UPLOAD_FILE_INFO_RESIDENT1",
        type: "file",
        index: 1,
        attachmentType: AttachmentType.AUTHORIZATION_DOCUMENT,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: [".pdf", ".bmp", ".jpg", ".jpeg"],
            errorMessage: UPLOAD_FILE_INVALID_EXTENSION,
          },
        },
      },
      bankAccountNumber: {
        name: "bankAccountNumber",
        label: "BANK_ACCOUNT_NUMBER",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(
                VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_AND_DIGITS_REGEX,
                ""
              ),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "BANK_ACCOUNT_NUMBER_SIZE",
          },
          maxLength: {
            value: 30,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: VIETNAM_ADDRESS_REGEX,
            errorMessage: "BANK_ACCOUNT_NUMBER_SIZE",
          },
          trim: true,
        },
      },
      bankAccountHolderName: {
        name: "bankAccountHolderName",
        label: "BANK_ACCOUNT_HOLDER_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_REGEX, ""),
          },
        ],
        rule: {
          isDisabled: true,
        },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      bankName: {
        name: "bankName",
        label: "BANK_NAME",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(VIETNAM_OTHER_CHARACTERS_THAN_PERMITTED_REGEX, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "BANK_NAME_SIZE",
          },
          maxLength: {
            value: 140,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: VIETNAM_ADDRESS_REGEX,
            errorMessage: "BANK_NAME_SIZE",
          },
          trim: true,
        },
      },
      bankAddress: {
        name: "bankAddress",
        label: "BANK_ADDRESS",
        type: "text",
        sanitize: [
          {
            mode: SanitizeMode.ON_CHANGE,
            sanitizeFn: (value) =>
              value.replace(VIETNAM_OTHER_CHARACTERS_THAN_ADDRESS_REGEX, ""),
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "BANK_ADDRESS_SIZE",
          },
          maxLength: {
            value: 140,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: VIETNAM_ADDRESS_REGEX_WITHOUT_PARENTHESIS,
            errorMessage: "BANK_ADDRESS_SIZE",
          },
          trim: true,
        },
      },
      dataProcessingConsent: {
        name: "dataProcessingConsent",
        header: "DATA_PROCESSING_HEADER",
        type: "static",
        staticValue: "DATA_PROCESSING_STATEMENT",
      },
      dataProcessingConsentAccepted1: {
        name: "dataProcessingConsentAccepted1",
        label: "DATA_PROCESSING_CONSENT1",
        type: "checkbox",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataProcessingConsentAccepted2: {
        name: "dataProcessingConsentAccepted2",
        label: "DATA_PROCESSING_CONSENT2",
        type: "checkbox",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataProcessingConsentAccepted3: {
        name: "dataProcessingConsentAccepted3",
        label: "DATA_PROCESSING_CONSENT3",
        type: "checkbox",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataProcessingConsentAccepted4: {
        name: "dataProcessingConsentAccepted4",
        label: "DATA_PROCESSING_CONSENT4",
        type: "checkbox",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataProcessingConsentAccepted5: {
        name: "dataProcessingConsentAccepted5",
        label: "DATA_PROCESSING_CONSENT5",
        type: "checkbox",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
      },
      dataPrivacyDisclosure: {
        name: "dataPrivacyDisclosure",
        type: "static",
        staticValue: "INFORMATION_DISCLOSURE",
      },
      marketingConsentAccepted: {
        name: "marketingConsentAccepted",
        label: "EXTRA_COMMUNICATION_CONSENT",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
    },
  },
  hideRulesOfConduct: true,
  footerLinks: ["PRIVACY", "COOKIE_POLICY", "COPYRIGHT"],
  footerLogos: [DaThongBaoLogo],
  deliveryPriceKeys: ["retail", "deliveryTotal", "tax", "totalAmountToPay"],
  showHmpBenefits: true,
  showHmpPriceContent: true,
  showGsgBenefits: true,
  showOnboardingDocumentsLink: true,
  showLinkToOrderConfirmation: true,
  showPdfContainer: true,
  showResidentRadioBox: false,
  showSocialMediaStatement: true,
  showOrderHandlerInformation: false,
  showPaymentAcknowledgment: true,
  showPaymentDisclaimer: true,
  showSponsorshipDisclaimer: true,
  supportedCountryCodes: ["vn"],
  defaultCountryCode: "+84|Vietnam",
  missingGsgStep: true,
} as const;
